import { useMemo } from "react";

import * as Cookies from "es-cookie";
import { useCookies } from "react-cookie";

import type { Locale, UserProfile } from "api/generated/model";

const ORG_PROFILE_COOKIE_NAME = "org_impero_profile";
export type OrgProfileCookie = UserProfile;

// Does not return a stateful value!
export function getOrgProfileCookie(): OrgProfileCookie | null {
    const cookies = Cookies.get(ORG_PROFILE_COOKIE_NAME);
    if (!cookies) {
        return null;
    }
    return parseOrgUserProfileCookie(JSON.parse(cookies));
}

type CookieKeys = typeof ORG_PROFILE_COOKIE_NAME;
interface Cookies {
    [ORG_PROFILE_COOKIE_NAME]?: OrgProfileCookie;
}

// Returns a stateful cookie value - an update to the cookie by the server will trigger a re-render
export const useOrgUserProfileCookie = (): OrgProfileCookie | null => {
    const [cookies] = useCookies<CookieKeys, Cookies>([
        ORG_PROFILE_COOKIE_NAME,
    ]);
    const jsonProfile = cookies[ORG_PROFILE_COOKIE_NAME];
    // Reparse the cookie only if it has changed
    return useMemo(
        () => (jsonProfile ? parseOrgUserProfileCookie(jsonProfile) : null),
        [jsonProfile]
    );
};

// Cookie value can be manipulated (in case of XSS for example), so we need to properly parse it.
// Parsing should be lenient (not throw an error if the cookie is malformed) since the format is not guaranteed.
function parseOrgUserProfileCookie(json: any): UserProfile {
    return {
        email: String(json.email),
        firstName: String(json.firstName),
        lastName: String(json.lastName),
        canLocalize: Boolean(json.canLocalize),
        userId: Number(json.userId),
        culture: String(json.culture) as Locale,
        timezone: String(json.timezone),
    };
}
